import {
  Building2,
  Users,
  Key,
  CreditCard,
  DollarSign,
  Database,
  Activity,
  AlertCircle,
  RefreshCw,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, Button } from '@/core/ui/components';
import { useAuth } from '@/app/providers';
import {
  MetricCard,
  RevenueChart,
  TenantGrowthChart,
  RecentTenantsTable,
} from '@/modules/dashboard/ui/components';
import { useDashboard } from '@/modules/dashboard/ui/hooks';

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

export function DashboardPage() {
  const { user } = useAuth();
  const {
    metrics,
    revenueChart,
    tenantGrowthChart,
    recentTenants,
    isLoading,
    refresh,
  } = useDashboard();

  const alerts = [
    { id: '1', type: 'warning', message: '3 tenants are approaching their user limit' },
    { id: '2', type: 'info', message: '5 invoices are pending payment' },
  ];

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white/90">
            Welcome back, {user?.name || 'Admin'}
          </h1>
          <p className="mt-1 text-gray-500 dark:text-gray-400">
            Here's what's happening with your platform today.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={refresh}
          leftIcon={<RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />}
        >
          Refresh
        </Button>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Monthly Recurring Revenue"
          value={`$${(metrics?.mrr || 0).toLocaleString()}`}
          icon={DollarSign}
          change={metrics?.mrrChange}
          iconBgClass="bg-emerald-100 dark:bg-emerald-900/30"
          iconClass="text-emerald-600 dark:text-emerald-400"
        />
        <MetricCard
          title="Active Tenants"
          value={metrics?.activeTenants || 0}
          icon={Building2}
          change={metrics?.tenantChange}
          iconBgClass="bg-indigo-100 dark:bg-indigo-900/30"
          iconClass="text-indigo-600 dark:text-indigo-400"
        />
        <MetricCard
          title="API Requests"
          value={formatNumber(metrics?.apiRequestsThisMonth || 0)}
          icon={Activity}
          change={metrics?.apiRequestsChange}
          changeLabel="this month"
          iconBgClass="bg-purple-100 dark:bg-purple-900/30"
          iconClass="text-purple-600 dark:text-purple-400"
        />
        <MetricCard
          title="Total Storage"
          value={formatBytes(metrics?.totalStorageBytes || 0)}
          icon={Database}
          change={metrics?.storageChange}
          iconBgClass="bg-amber-100 dark:bg-amber-900/30"
          iconClass="text-amber-600 dark:text-amber-400"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {revenueChart && (
          <RevenueChart data={revenueChart} isLoading={isLoading} />
        )}
        {tenantGrowthChart && (
          <TenantGrowthChart data={tenantGrowthChart} isLoading={isLoading} />
        )}
      </div>

      {/* Bottom Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Tenants */}
        <div className="lg:col-span-2">
          <RecentTenantsTable tenants={recentTenants} isLoading={isLoading} />
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Alerts */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90 mb-4">
              Alerts
            </h3>
            <div className="space-y-3">
              {alerts.map((alert) => (
                <div
                  key={alert.id}
                  className={`
                    flex items-start gap-3 p-3 rounded-lg
                    ${alert.type === 'warning'
                      ? 'bg-amber-50 dark:bg-amber-900/20'
                      : 'bg-blue-50 dark:bg-blue-900/20'
                    }
                  `}
                >
                  <AlertCircle
                    className={`w-5 h-5 shrink-0 ${
                      alert.type === 'warning' ? 'text-amber-500' : 'text-blue-500'
                    }`}
                  />
                  <p
                    className={`text-sm ${
                      alert.type === 'warning'
                        ? 'text-amber-700 dark:text-amber-300'
                        : 'text-blue-700 dark:text-blue-300'
                    }`}
                  >
                    {alert.message}
                  </p>
                </div>
              ))}
            </div>
          </Card>

          {/* Quick Actions */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-white/90 mb-4">
              Quick Actions
            </h3>
            <div className="space-y-2">
              <Link to="/tenants/new" className="block">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  leftIcon={<Building2 className="w-4 h-4" />}
                >
                  Add New Tenant
                </Button>
              </Link>
              <Link to="/api-keys" className="block">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  leftIcon={<Key className="w-4 h-4" />}
                >
                  Generate API Key
                </Button>
              </Link>
              <Link to="/billing" className="block">
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  leftIcon={<CreditCard className="w-4 h-4" />}
                >
                  Manage Plans
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
